import CheckIcon from "@mui/icons-material/Check";
import { styled } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import Button from "components-redesign/Button/Button";
import { FIGMA_COLORS } from "components-redesign/styles";
import { COLORS_TO_BE_REVIEWED } from "components-redesign/styles/colors";
import {
  TypographyBodySmall,
  TypographyLabelSmall,
} from "components-redesign/Typography/Typography";
import { Tooltip } from "components-redesign/Tooltip/Tooltip";
import { FlexContent } from "components/FlexContent/FlexContent";
import { DatagridFilter } from "./types";
import { filterOptionsBySearch } from "./utils";

const FilterList = styled("div")({
  maxHeight: "250px",
  overflowY: "auto",
  display: "flex",
  flexDirection: "column",
  gap: "4px",
});

const FilterItem = styled("div")<{ isDisabled?: boolean }>(
  ({ isDisabled }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "8px 12px",
    cursor: isDisabled ? "default" : "pointer",
    gap: "10px",
    color: isDisabled ? FIGMA_COLORS.onSurfaceSubtleLight : "inherit",
    opacity: isDisabled ? 0.38 : 1,
    "&:hover": {
      backgroundColor: isDisabled ? "transparent" : "#f5f5f5",
    },
  })
);

const SearchHint = styled(TypographyBodySmall)({
  color: COLORS_TO_BE_REVIEWED.neutral70,
  marginTop: "4px",
});

const SelectAllItem = styled("div")({
  padding: "8px 12px",
  cursor: "pointer",
  fontSize: "12px",
  color: FIGMA_COLORS.primaryLight,
  borderBottom: "1px solid #eee",
  marginBottom: "4px",
  "&:hover": {
    backgroundColor: "#f5f5f5",
  },
});

const CheckmarkContainer = styled("div")<{ checked: boolean }>(
  ({ checked }) => ({
    width: "20px",
    height: "20px",
    marginRight: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: checked ? "#4CAF50" : "transparent",
  })
);

const FilterActions = styled("div")({
  display: "flex",
  justifyContent: "flex-end",
  marginTop: "8px",
  gap: "8px",
});

export interface FilterContentProps {
  options: DatagridFilter[];
  selectedValues: string[];
  onSelectionChange: (values: string[]) => void;
  onApply: () => void;
  onClear: () => void;
  hideFilterCount?: boolean;
  hideSearch?: boolean;
  getOptionDisabledReason?: (
    value: string,
    selectedValues: string[]
  ) => string | undefined;
  searchTerm: string;
  onSearchChange: (value: string) => void;
}

const getVisibleItems = (
  itemHeights: number[],
  scrollPosition: number,
  containerHeight: number
) => {
  const threshold = 8;

  let acum = 0;
  let indexStart = -1;
  let indexEnd = -1;

  let marginTop = 0;
  let marginBottom = 0;

  for (let i = 0; i < itemHeights.length; i++) {
    acum += itemHeights[i];

    if (acum > scrollPosition && indexStart === -1) {
      indexStart = i;
    }

    if (acum > scrollPosition + containerHeight) {
      indexEnd = i;
      break;
    }
  }

  if (indexEnd === -1 || indexStart === -1) {
    indexEnd = itemHeights.length - 1;
    indexStart = indexEnd;

    let a = 0;

    for (let i = itemHeights.length - 1; i >= 0; i--) {
      if (a > containerHeight) break;

      a += itemHeights[i];
      indexStart--;
    }
  }

  const visible: boolean[] = [];
  let filling: "top" | "bottom" = "top";

  for (let i = 0; i < itemHeights.length; i++) {
    let pushing = false;

    if (i >= indexStart - threshold && i <= indexEnd + threshold) {
      pushing = true;
    }

    visible.push(pushing);

    if (pushing) {
      filling = "bottom";
    } else {
      if (filling === "top") {
        marginTop += itemHeights[i];
      } else {
        marginBottom += itemHeights[i];
      }
    }
  }

  return {
    marginTop,
    marginBottom,
    visible,
  };
};

export const FilterContent: React.FC<FilterContentProps> = ({
  options,
  selectedValues,
  onSelectionChange,
  onApply,
  onClear,
  hideFilterCount,
  hideSearch = false,
  getOptionDisabledReason,
  searchTerm,
  onSearchChange,
}) => {
  const ITEM_HEIGHT = 50;
  const CONTAINER_HEIGHT = 250;

  const guid = useMemo(() => new Date().getTime(), []);

  const [visibleItems, setVisibleItems] = useState<boolean[]>([]);
  const [virtualMargins, setVirtualMargins] = useState<{
    top: number;
    bottom: number;
  }>({ top: 0, bottom: 0 });

  const filteredOptions = useMemo(
    () => filterOptionsBySearch(options, searchTerm),
    [options, searchTerm]
  );

  const calculateVisibleItems = useCallback(() => {
    const itemHeights = new Array(filteredOptions.length).fill(ITEM_HEIGHT);
    const filterList = document.querySelector(`#filter-list-${guid}`);
    if (!filterList) return;

    const scrollTop = filterList.scrollTop || 0;

    const { visible, marginTop, marginBottom } = getVisibleItems(
      itemHeights,
      scrollTop,
      CONTAINER_HEIGHT
    );

    setVisibleItems(visible);
    setVirtualMargins({ top: marginTop, bottom: marginBottom });
  }, [filteredOptions, guid]);

  const disabledReasonOf = (option: DatagridFilter) =>
    selectedValues.includes(option.value)
      ? undefined
      : getOptionDisabledReason?.(option.value, selectedValues);

  const handleSelectAll = () => {
    if (selectedValues.length === 0) {
      onSelectionChange(
        filteredOptions.reduce<string[]>(
          (acc, option) =>
            getOptionDisabledReason?.(option.value, acc)
              ? acc
              : [...acc, option.value],
          []
        )
      );
    } else {
      onSelectionChange([]);
    }
  };

  const handleItemToggle = (option: DatagridFilter) => {
    if (selectedValues.includes(option.value)) {
      onSelectionChange(selectedValues.filter((item) => item !== option.value));
      return;
    }
    if (disabledReasonOf(option)) {
      return;
    }
    onSelectionChange([...selectedValues, option.value]);
  };

  useEffect(() => {
    calculateVisibleItems();

    const filterList = document.querySelector(`#filter-list-${guid}`);
    if (!filterList) return;

    const handleScroll = () => {
      calculateVisibleItems();
    };

    filterList.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      filterList.removeEventListener("scroll", handleScroll);
    };
  }, [calculateVisibleItems, guid]);

  return (
    <>
      {!hideSearch && (
        <>
          <TextField
            size="small"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            fullWidth
          />

          {!!searchTerm && (
            <SearchHint>
              Separate multiple values with a comma, space or line break
            </SearchHint>
          )}
        </>
      )}

      <SelectAllItem onClick={handleSelectAll}>
        <TypographyLabelSmall>
          {selectedValues.length === 0
            ? `Select all`
            : `Deselect all- ${selectedValues.length}`}
        </TypographyLabelSmall>
      </SelectAllItem>

      <FilterList id={`filter-list-${guid}`}>
        {virtualMargins.top > 0 && (
          <div style={{ height: virtualMargins.top, flexShrink: 0 }} />
        )}
        {filteredOptions.map((option, i) => {
          if (!visibleItems?.[i]) return null;

          const disabledReason = disabledReasonOf(option);

          const item = (
            <FilterItem
              key={`datagrid-filter-item-${i}`}
              data-qa={`datagrid-filter-item_${option.value}`}
              isDisabled={!!disabledReason}
              onClick={() => handleItemToggle(option)}>
              <FlexContent
                align="center"
                style={{ minWidth: 0, flex: 1 }}>
                <CheckmarkContainer
                  checked={selectedValues.includes(option.value)}>
                  <CheckIcon fontSize="small" />
                </CheckmarkContainer>
                {option.renderLabel ? (
                  <div
                    style={{
                      overflow: "hidden",
                      maxWidth: "100%",
                      minWidth: 0,
                      flex: 1,
                    }}>
                    {option.renderLabel()}
                  </div>
                ) : (
                  <span
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}>
                    {option.value}
                  </span>
                )}
              </FlexContent>

              {!hideFilterCount && (
                <TypographyBodySmall
                  style={{
                    justifySelf: "flex-end",
                    color: COLORS_TO_BE_REVIEWED.neutral70,
                  }}>
                  {option.count}
                </TypographyBodySmall>
              )}
            </FilterItem>
          );

          return disabledReason ? (
            <Tooltip
              key={`datagrid-filter-item-${i}`}
              tooltipContent={{ content: disabledReason }}>
              {item}
            </Tooltip>
          ) : (
            item
          );
        })}
        {virtualMargins.bottom > 0 && (
          <div style={{ height: virtualMargins.bottom, flexShrink: 0 }} />
        )}
      </FilterList>

      <FilterActions>
        <Button
          variant="text"
          size="small"
          onClick={onClear}>
          Clear
        </Button>
        <Button
          size="small"
          onClick={onApply}>
          Apply
        </Button>
      </FilterActions>
    </>
  );
};
